import { useEffect, useMemo, useState } from "react";
import {
  Archive, ChevronDown, ChevronRight, Copy, Download,
  FileCode2, Globe, Image as ImageIcon, Link2, Save,
  Trash2, Wand2, Plus, Languages, RefreshCw, CheckCircle2,
} from "lucide-react";
import { toast } from "react-toastify";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { useNavigate } from "react-router-dom";
import ConfirmDialog from "../components/ConfirmDialog";

// ─── Classes padrão ───────────────────────────────────────────────────────────

const inputCls =
  "w-full px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 bg-transparent text-sm text-slate-900 dark:text-slate-100 placeholder:text-slate-400";

const labelCls =
  "block text-xs mb-1 text-slate-600 dark:text-slate-300 font-medium";

const cardCls =
  "rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-4";

// ─── Utilitários ──────────────────────────────────────────────────────────────

function slugify(value = "") {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .replace(/-{2,}/g, "-");
}

function escapeHtml(str = "") {
  return String(str)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;")
    .replaceAll("'", "&#39;");
}

function nl2br(str = "") {
  return escapeHtml(str).replace(/\n/g, "<br>");
}

function buildSeoBlockFromText(text = "") {
  if (!text.trim()) return "";
  return text
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter(Boolean)
    .map((p) => `<p>${escapeHtml(p)}</p>`)
    .join("\n      ");
}

// ─── Traduções ────────────────────────────────────────────────────────────────

const TRANSLATIONS = {
  en: { cookie_title: "We use cookies!", cookie_text: "This website uses cookies to ensure you get the best experience.", cta_label: "Go to Official Site", disclosure_text: "We are an affiliate website. We do not sell or ship products. Purchases, warranty, and support are provided exclusively on the official website.", learn_more: "Learn more", decline: "Decline", accept: "Accept", privacy: "Privacy", contact: "Contact", official_support: "Official Support", disclosure_label: "Disclosure:" },
  pt: { cookie_title: "Usamos cookies!", cookie_text: "Este site usa cookies para garantir que você tenha a melhor experiência.", cta_label: "Ir para o Site Oficial", disclosure_text: "Somos um site de afiliados. Não vendemos nem enviamos produtos. Compras, garantia e suporte são fornecidos exclusivamente no site oficial.", learn_more: "Saiba mais", decline: "Recusar", accept: "Aceitar", privacy: "Privacidade", contact: "Contato", official_support: "Suporte Oficial", disclosure_label: "Aviso:" },
  es: { cookie_title: "¡Usamos cookies!", cookie_text: "Este sitio web utiliza cookies para garantizar que obtenga la mejor experiencia.", cta_label: "Ir al Sitio Oficial", disclosure_text: "Somos un sitio web de afiliados. No vendemos ni enviamos productos. Las compras, garantía y soporte se proporcionan exclusivamente en el sitio web oficial.", learn_more: "Más información", decline: "Rechazar", accept: "Aceptar", privacy: "Privacidad", contact: "Contacto", official_support: "Soporte Oficial", disclosure_label: "Divulgación:" },
  fr: { cookie_title: "Nous utilisons des cookies!", cookie_text: "Ce site web utilise des cookies pour vous garantir la meilleure expérience.", cta_label: "Aller au site officiel", disclosure_text: "Nous sommes un site web d'affiliation. Nous ne vendons ni n'expédions de produits. Les achats, la garantie et le support sont fournis exclusivement sur le site officiel.", learn_more: "En savoir plus", decline: "Refuser", accept: "Accepter", privacy: "Confidentialité", contact: "Contact", official_support: "Support Officiel", disclosure_label: "Divulgation:" },
  de: { cookie_title: "Wir verwenden Cookies!", cookie_text: "Diese Website verwendet Cookies, um Ihnen die beste Erfahrung zu gewährleisten.", cta_label: "Zur offiziellen Website", disclosure_text: "Wir sind eine Affiliate-Website. Wir verkaufen oder versenden keine Produkte. Käufe, Garantie und Support werden ausschließlich auf der offiziellen Website bereitgestellt.", learn_more: "Mehr erfahren", decline: "Ablehnen", accept: "Akzeptieren", privacy: "Datenschutz", contact: "Kontakt", official_support: "Offizieller Support", disclosure_label: "Offenlegung:" },
  it: { cookie_title: "Utilizziamo i cookie!", cookie_text: "Questo sito web utilizza i cookie per garantirti la migliore esperienza.", cta_label: "Vai al sito ufficiale", disclosure_text: "Siamo un sito web di affiliazione. Non vendiamo né spediamo prodotti. Acquisti, garanzia e supporto sono forniti esclusivamente sul sito ufficiale.", learn_more: "Per saperne di più", decline: "Rifiuta", accept: "Accetta", privacy: "Privacy", contact: "Contatto", official_support: "Supporto Ufficiale", disclosure_label: "Divulgazione:" },
};

const LANGUAGE_OPTIONS = [
  { code: "en", name: "English", flag: "🇺🇸" },
  { code: "pt", name: "Português", flag: "🇧🇷" },
  { code: "es", name: "Español", flag: "🇪🇸" },
  { code: "fr", name: "Français", flag: "🇫🇷" },
  { code: "de", name: "Deutsch", flag: "🇩🇪" },
  { code: "it", name: "Italiano", flag: "🇮🇹" },
];

const INITIAL_FORM = {
  project_name: "", slug: "", html_file_name: "pv.html",
  page_title: "", meta_description: "", robots: "noindex,nofollow",
  language: "en", product_url: "", affiliate_link: "",
  desktop_modal_link: "", mobile_modal_link: "", cta_bar_link: "",
  learn_more_link: "", privacy_link: "", contact_link: "",
  official_support_link: "", custom_script: "",
  bg_desktop: "", bg_tablet: "", bg_mobile: "", bg_size: "cover",
  overlay_desktop: "0.70", overlay_mobile: "0.60",
  cookie_title: "", cookie_text: "", cta_label: "", disclosure_text: "", seo_block: "",
};

// ─── HTML Generator ───────────────────────────────────────────────────────────

function generatePresellHtml(data) {
  const t = TRANSLATIONS[data.language] || TRANSLATIONS.en;
  const defaultDesktopLink = data.product_url.trim();
  const defaultMobileLink = data.affiliate_link.trim() || defaultDesktopLink;
  const desktopModalLink = data.desktop_modal_link.trim() || defaultDesktopLink;
  const mobileModalLink = data.mobile_modal_link.trim() || defaultMobileLink;
  const ctaBarLink = data.cta_bar_link.trim() || defaultDesktopLink;
  const learnMoreLink = data.learn_more_link.trim() || defaultDesktopLink;
  const privacyLink = data.privacy_link.trim() || "/privacy";
  const contactLink = data.contact_link.trim() || "/contact";
  const officialSupportLink = data.official_support_link.trim() || defaultDesktopLink;
  const robots = data.robots.trim() || "noindex,nofollow";
  const bgSize = data.bg_size.trim() || "cover";
  const seoBlock = buildSeoBlockFromText(data.seo_block);
  const customScriptBlock = data.custom_script?.trim() ? `\n  ${data.custom_script.trim()}\n` : "";

  return `<!doctype html>
<html lang="${data.language || 'en'}">
<head>${customScriptBlock}
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>${escapeHtml(data.page_title)}</title>
  <meta name="robots" content="${escapeHtml(robots)}" />
  <meta name="description" content="${escapeHtml(data.meta_description)}" />

  <style>
    :root {
      --overlay-desktop: ${escapeHtml(data.overlay_desktop || "0.70")};
      --overlay-mobile: ${escapeHtml(data.overlay_mobile || "0.60")};
      --text: #0f172a;
      --white: #ffffff;
      --shadow: 0 8px 32px rgba(0,0,0,.20);
      --radius: 18px;
    }
    * { box-sizing: border-box; }
    html, body { margin: 0; padding: 0; min-height: 100%; font-family: Roboto, Arial, Helvetica, sans-serif; background: #000; }
    body { color: var(--text); }
    .page { position: relative; min-height: 100vh; width: 100%; background-image: linear-gradient(rgba(0,0,0,var(--overlay-desktop)), rgba(0,0,0,var(--overlay-desktop))), url('${escapeHtml(data.bg_desktop)}'); background-repeat: no-repeat; background-position: top center; background-size: ${escapeHtml(bgSize)}; }
    .sr-only-seo { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0, 0, 0, 0); white-space: nowrap; border: 0; }
    .cookie-backdrop { position: fixed; inset: 0; display: flex; align-items: center; justify-content: center; background: rgba(0,0,0,.35); z-index: 999; padding: 16px; }
    .cookie-modal, .cookie-mobile { background: #fff; border-radius: 18px; box-shadow: var(--shadow); width: min(340px, 95vw); text-align: center; font-family: Arial, Helvetica, sans-serif; }
    .cookie-modal { padding: 24px 18px 14px; }
    .cookie-mobile { position: fixed; left: 50%; bottom: 30px; transform: translateX(-50%); padding: 24px 18px 14px; z-index: 1000; display: none; max-width: 95vw; }
    .cookie-emoji { font-size: 2rem; line-height: 1; }
    .cookie-title { margin: 10px 0 4px; font-size: 1.15rem; color: #222; }
    .cookie-text { font-size: .97rem; color: #444; margin-bottom: 14px; }
    .cookie-text a { color: #4F8CFF; text-decoration: underline; }
    .cookie-actions { display: flex; justify-content: center; gap: 10px; flex-wrap: wrap; }
    .cookie-btn { display: inline-block; border: none; border-radius: 9px; padding: 9px 18px; font-size: 1rem; font-weight: 600; text-decoration: none; cursor: pointer; }
    .cookie-btn.secondary { background: #F0F0F0; color: #333; }
    .cookie-btn.primary { background: #4F8CFF; color: #fff; }
    .affiliate-bar { position: fixed; left: 50%; transform: translateX(-50%); bottom: 14px; z-index: 9999; width: calc(100% - 24px); max-width: 940px; padding: 14px 16px; background: #fff; color: #0f172a; border-radius: 14px; box-shadow: 0 8px 28px rgba(0,0,0,.18); font: 14px/1.35 system-ui, -apple-system, Segoe UI, Roboto, Arial, sans-serif; }
    .affiliate-inner { display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 10px; }
    .affiliate-copy { flex: 1 1 240px; min-width: 220px; }
    .affiliate-actions { display: flex; gap: 8px; flex-wrap: wrap; }
    .affiliate-link { display: inline-block; text-decoration: none; border: 1px solid #e5e7eb; padding: 8px 12px; border-radius: 10px; color: #0f172a; background: #fff; }
    .affiliate-link.primary { background: #111827; color: #fff; border-color: #111827; font-weight: 600; padding: 8px 14px; }
    @media (max-width: 1024px) { .page { background-image: linear-gradient(rgba(0,0,0,var(--overlay-desktop)), rgba(0,0,0,var(--overlay-desktop))), url('${escapeHtml(data.bg_tablet)}'); } }
    @media (max-width: 767px) { .page { background-image: linear-gradient(rgba(0,0,0,var(--overlay-mobile)), rgba(0,0,0,var(--overlay-mobile))), url('${escapeHtml(data.bg_mobile)}'); } .cookie-backdrop, .affiliate-bar { display: none; } .cookie-mobile { display: block; } }
  </style>
</head>
<body>
  <main class="page">
    <div class="sr-only-seo">
      ${seoBlock}
    </div>
  </main>

  <div class="cookie-backdrop" id="cookieBackdrop">
    <div class="cookie-modal">
      <div class="cookie-emoji">🍪</div>
      <h3 class="cookie-title">${escapeHtml(data.cookie_title || t.cookie_title)}</h3>
      <div class="cookie-text">${nl2br(data.cookie_text || t.cookie_text)} <a href="${escapeHtml(learnMoreLink)}">${escapeHtml(t.learn_more)}</a></div>
      <div class="cookie-actions">
        <a class="cookie-btn secondary" href="${escapeHtml(desktopModalLink)}">${escapeHtml(t.decline)}</a>
        <a class="cookie-btn primary" href="${escapeHtml(desktopModalLink)}">${escapeHtml(t.accept)}</a>
      </div>
    </div>
  </div>

  <div class="cookie-mobile" id="cookieMobile">
    <div class="cookie-emoji">🍪</div>
    <h3 class="cookie-title">${escapeHtml(data.cookie_title || t.cookie_title)}</h3>
    <div class="cookie-text">${nl2br(data.cookie_text || t.cookie_text)}</div>
    <div class="cookie-actions">
      <a class="cookie-btn secondary" href="${escapeHtml(mobileModalLink)}">${escapeHtml(t.decline)}</a>
      <a class="cookie-btn primary" href="${escapeHtml(mobileModalLink)}">${escapeHtml(t.accept)}</a>
    </div>
  </div>

  <div class="affiliate-bar">
    <div class="affiliate-inner">
      <div class="affiliate-copy"><strong>${escapeHtml(t.disclosure_label)}</strong> ${nl2br(data.disclosure_text || t.disclosure_text)}</div>
      <div class="affiliate-actions">
        <a class="affiliate-link" href="${escapeHtml(privacyLink)}">${escapeHtml(t.privacy)}</a>
        <a class="affiliate-link" href="${escapeHtml(contactLink)}">${escapeHtml(t.contact)}</a>
        <a class="affiliate-link" href="${escapeHtml(officialSupportLink)}" target="_blank" rel="noopener">${escapeHtml(t.official_support)}</a>
        <a class="affiliate-link primary" href="${escapeHtml(ctaBarLink)}" target="_blank" rel="noopener">${escapeHtml(data.cta_label || t.cta_label)}</a>
      </div>
    </div>
  </div>
</body>
</html>`;
}

// ─── AccordionSection ─────────────────────────────────────────────────────────

function AccordionSection({ title, icon, isOpen, onToggle, children, rightSlot = null }) {
  const Icon = icon;
  return (
    <section className={cardCls}>
      <button type="button" onClick={onToggle} className="w-full flex items-center justify-between gap-3 text-left">
        <div className="flex items-center gap-2">
          {Icon ? <Icon className="w-4 h-4 text-slate-500 dark:text-slate-400" /> : null}
          <h2 className="text-sm font-bold text-slate-900 dark:text-slate-100">{title}</h2>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {rightSlot}
          {isOpen ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
        </div>
      </button>
      {isOpen && <div className="mt-4">{children}</div>}
    </section>
  );
}

// ─── Status Badge ─────────────────────────────────────────────────────────────

function PageStatusBadge({ status }) {
  if (status === "published") return (
    <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
      <CheckCircle2 className="w-3.5 h-3.5" /> Publicada
    </span>
  );
  if (status === "archived") return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">Arquivada</span>
  );
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400">Rascunho</span>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

export default function SobePages() {
  const navigate = useNavigate();

  const [form, setForm] = useState({ ...INITIAL_FORM });
  const [savedProjects, setSavedProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(null);
  const [generatedHtml, setGeneratedHtml] = useState("");
  const [analysisLoading, setAnalysisLoading] = useState(false);
  const [detectedData, setDetectedData] = useState(null);

  const [scripts, setScripts] = useState([]);
  const [loadingScripts, setLoadingScripts] = useState(false);
  const [selectedScriptUuid, setSelectedScriptUuid] = useState("");
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState("");

  const [domains, setDomains] = useState([]);
  const [selectedDomainId, setSelectedDomainId] = useState("");
  const [savingToSobeVenda, setSavingToSobeVenda] = useState(false);
  const [publishingToSobeVenda, setPublishingToSobeVenda] = useState(false);
  const [savedPageRecord, setSavedPageRecord] = useState(null);
  const [savedPages, setSavedPages] = useState([]);
  const [loadingSavedPages, setLoadingSavedPages] = useState(false);
  const [pageActionLoadingId, setPageActionLoadingId] = useState(null);

  // ConfirmDialogs
  const [confirmDeletePageId, setConfirmDeletePageId] = useState(null);
  const [confirmArchivePageId, setConfirmArchivePageId] = useState(null);

  const [openSections, setOpenSections] = useState({
    analysis: true, pageInfo: false, links: false,
    visuals: false, preview: false, html: false,
  });

  const [user, setUser] = useState(null);

  const API_BASE_AFFILIATE = "https://api.sobevenda.com.br/api-proxy/affiliate";
  const API_BASE_SOBEPAGES = "https://api.sobevenda.com.br/api-proxy/sobepages";
  const API_BASE_SCRIPT    = "https://api.sobevenda.com.br/api-proxy/script";

  // ── API helpers ─────────────────────────────────────────────────────────────

  async function apiFetch(base, path, options = {}) {
    const res = await fetch(`${base}${path}`, {
      ...options,
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("sobevenda_token")}`, ...(options.headers || {}) },
    });
    const text = await res.text();
    let data = null;
    try { data = text ? JSON.parse(text) : null; } catch { data = text; }
    if (!res.ok) throw new Error(data?.message || data?.error || "Erro na requisição.");
    return data;
  }

  const apiAffiliate = (path, opts) => apiFetch(API_BASE_AFFILIATE, path, opts);
  const apiSobePages = (path, opts) => apiFetch(API_BASE_SOBEPAGES, path, opts);
  const apiScript    = (path, opts) => apiFetch(API_BASE_SCRIPT, path, opts);

  // ── Load functions ──────────────────────────────────────────────────────────

  async function loadUser() {
    try {
      const data = await apiAffiliate("/auth/me", { method: "GET" });
      setUser(data);
    } catch { setUser(null); }
  }

  async function loadScripts() {
    setLoadingScripts(true);
    try {
      const data = await apiScript("/list-sobescripts-active", { method: "GET" });
      setScripts(Array.isArray(data) ? data : Array.isArray(data?.scripts) ? data.scripts : []);
    } catch { toast.error("Não foi possível carregar os scripts."); setScripts([]); }
    finally { setLoadingScripts(false); }
  }

  async function loadProducts() {
    setLoadingProducts(true);
    try {
      const res = await fetch("https://api.sobevenda.com.br/api-proxy/products/list", {
        method: "GET",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("sobevenda_token")}` },
      });
      const data = await res.json();
      setProducts(Array.isArray(data?.products) ? data.products : []);
    } catch { toast.error("Não foi possível carregar os produtos."); setProducts([]); }
    finally { setLoadingProducts(false); }
  }

  async function loadSavedPages() {
    setLoadingSavedPages(true);
    try {
      const data = await apiSobePages("/pages", { method: "GET" });
      const list = Array.isArray(data) ? data : [];
      setSavedPages([...list].sort((a, b) => Number(b.created_at || 0) - Number(a.created_at || 0)));
    } catch { toast.error("Não foi possível carregar as páginas salvas."); setSavedPages([]); }
    finally { setLoadingSavedPages(false); }
  }

  async function loadConnectedDomains() {
    try {
      const data = await apiSobePages("/domains", { method: "GET" });
      const list = Array.isArray(data) ? data : [];
      setDomains(list);
      const connected = list.find((d) => d.is_active === true || d.status === "connected" || d.status === "active");
      setSelectedDomainId(connected ? String(connected.id) : "");
    } catch { toast.error("Não foi possível carregar os domínios."); setDomains([]); }
  }

  useEffect(() => {
    try {
      const raw = localStorage.getItem("sobepages_projects");
      const parsed = raw ? JSON.parse(raw) : [];
      setSavedProjects(Array.isArray(parsed) ? parsed : []);
    } catch { setSavedProjects([]); }
  }, []);

  useEffect(() => {
    loadUser();
    loadConnectedDomains();
    loadSavedPages();
    loadScripts();
    loadProducts();
  }, []);

  useEffect(() => {
    if (!savedPageRecord?.id || scripts.length === 0) { setSelectedScriptUuid(""); return; }
    const linked = scripts.find((s) => Number(s.pages_id) === Number(savedPageRecord.id));
    setSelectedScriptUuid(linked?.uuid || "");
  }, [savedPageRecord, scripts]);

  // ── Handlers ────────────────────────────────────────────────────────────────

  function handleLanguageChange(newLanguage) {
    const t = TRANSLATIONS[newLanguage] || TRANSLATIONS.en;
    setForm((prev) => ({
      ...prev, language: newLanguage,
      cookie_title: prev.cookie_title || t.cookie_title,
      cookie_text: prev.cookie_text || t.cookie_text,
      cta_label: prev.cta_label || t.cta_label,
      disclosure_text: prev.disclosure_text || t.disclosure_text,
    }));
    const langName = LANGUAGE_OPTIONS.find((l) => l.code === newLanguage)?.name;
    toast.success(`Idioma alterado para ${langName}`);
  }

  function handleUsePageInEdition(page) {
    setSavedPageRecord(page);
    if (page?.domains_id) setSelectedDomainId(String(page.domains_id));
    const linked = scripts.find((s) => Number(s.pages_id) === Number(page.id));
    setSelectedScriptUuid(linked?.uuid || "");
    toast.success("Página carregada na edição.");
  }

  function handleSelectProduct(productId) {
    setSelectedProductId(productId);
    if (!productId) return;
    const product = products.find((p) => p.id === Number(productId));
    if (!product) return;
    setForm((prev) => ({
      ...prev,
      product_url: product.producer_url || prev.product_url,
      affiliate_link: product.affiliate_link || prev.affiliate_link,
      project_name: product.name || prev.project_name,
      slug: slugify(product.name) || prev.slug,
    }));
    toast.success(`Produto "${product.name}" selecionado!`);
  }

  function toggleSection(key) {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  function setField(name, value) {
    setForm((prev) => {
      const next = { ...prev, [name]: value };
      if (name === "project_name" && !prev.slug.trim()) next.slug = slugify(value);
      return next;
    });
  }

  function persistProjects(projects) {
    setSavedProjects(projects);
    localStorage.setItem("sobepages_projects", JSON.stringify(projects));
  }

  async function handleAnalyzeProductPage() {
    if (!form.product_url.trim()) { toast.error("Preencha o link do produtor."); return; }
    if (!form.affiliate_link.trim()) { toast.error("Preencha o link de afiliado."); return; }
    try {
      setAnalysisLoading(true);
      const res = await fetch("https://api.sobevenda.com.br/api-proxy/sobepages/analyze", {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("sobevenda_token")}`, "Content-Type": "application/json" },
        body: JSON.stringify({ product_url: form.product_url.trim(), affiliate_link: form.affiliate_link.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.details || data?.error || "Falha ao analisar a página.");
      setDetectedData(data);
      setForm((prev) => ({
        ...prev,
        project_name: data.product_name || prev.project_name,
        slug: data.slug || prev.slug,
        page_title: data.page_title || prev.page_title,
        meta_description: data.meta_description || prev.meta_description,
        bg_desktop: data.bg_desktop || prev.bg_desktop,
        bg_tablet: data.bg_tablet || prev.bg_tablet,
        bg_mobile: data.bg_mobile || prev.bg_mobile,
        seo_block: data.seo_block || prev.seo_block,
        desktop_modal_link: data.desktop_modal_link || prev.desktop_modal_link,
        mobile_modal_link: data.mobile_modal_link || prev.mobile_modal_link,
        cta_bar_link: data.cta_bar_link || prev.cta_bar_link,
        learn_more_link: data.learn_more_link || prev.learn_more_link,
        privacy_link: data.privacy_link || prev.privacy_link,
        contact_link: data.contact_link || prev.contact_link,
        official_support_link: data.official_support_link || prev.official_support_link,
      }));
      toast.success("Página analisada com sucesso.");
    } catch (error) {
      toast.error(error.message || "Não foi possível analisar a página.");
    } finally {
      setAnalysisLoading(false);
    }
  }

  function handleGenerateHtml() {
    if (!form.page_title.trim()) { toast.error("Preencha o Page Title."); return; }
    if (!form.meta_description.trim()) { toast.error("Preencha a Meta Description."); return; }
    if (!form.product_url.trim()) { toast.error("Preencha o link do produtor."); return; }
    if (!form.affiliate_link.trim()) { toast.error("Preencha o link de afiliado."); return; }
    if (!form.bg_desktop.trim() || !form.bg_tablet.trim() || !form.bg_mobile.trim()) {
      toast.error("Preencha os 3 backgrounds: desktop, tablet e mobile."); return;
    }
    setGeneratedHtml(generatePresellHtml(form));
    toast.success("HTML gerado com sucesso.");
  }

  async function handleCopyHtml() {
    if (!generatedHtml) { toast.error("Gere o HTML antes de copiar."); return; }
    try { await navigator.clipboard.writeText(generatedHtml); toast.success("Código HTML copiado."); }
    catch { toast.error("Não foi possível copiar o HTML."); }
  }

  function handleDownloadHtml() {
    if (!generatedHtml) { toast.error("Gere o HTML antes de baixar."); return; }
    const fileName = (form.html_file_name || "pv.html").trim().endsWith(".html") ? form.html_file_name.trim() : `${(form.html_file_name || "pv").trim()}.html`;
    const blob = new Blob([generatedHtml], { type: "text/html;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url; link.download = fileName;
    document.body.appendChild(link); link.click(); document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success("Arquivo HTML baixado.");
  }

  async function handleDownloadZip() {
    if (!generatedHtml) { toast.error("Gere o HTML antes de baixar o ZIP."); return; }
    if (!form.bg_desktop || !form.bg_tablet || !form.bg_mobile) { toast.error("Os backgrounds precisam estar preenchidos para gerar o ZIP."); return; }
    try {
      const zip = new JSZip();
      const htmlFileName = (form.html_file_name || "pv.html").trim().endsWith(".html") ? form.html_file_name.trim() : `${(form.html_file_name || "pv").trim()}.html`;
      const slugBase = form.slug?.trim() || slugify(form.project_name || "sobepage");
      const zippedHtml = generatedHtml.replaceAll(form.bg_desktop, "desktop.webp").replaceAll(form.bg_tablet, "tablet.webp").replaceAll(form.bg_mobile, "mobile.webp");
      const [d, t, m] = await Promise.all([fetch(form.bg_desktop).then(r => r.blob()), fetch(form.bg_tablet).then(r => r.blob()), fetch(form.bg_mobile).then(r => r.blob())]);
      zip.file(htmlFileName, zippedHtml); zip.file("desktop.webp", d); zip.file("tablet.webp", t); zip.file("mobile.webp", m);
      const zipBlob = await zip.generateAsync({ type: "blob" });
      saveAs(zipBlob, `${slugBase || "sobepage"}.zip`);
      toast.success("ZIP baixado com sucesso.");
    } catch (error) { toast.error(error.message || "Não foi possível gerar o ZIP."); }
  }

  async function handleSaveToSobeVenda() {
    if (!form.project_name.trim()) { toast.error("Preencha o nome do projeto."); return; }
    if (!form.slug.trim()) { toast.error("Preencha o slug da página."); return; }
    if (!generatedHtml.trim()) { toast.error("Gere o HTML antes de salvar no SobeVenda."); return; }
    if (!savedPageRecord?.id && user?.max_active_pages !== undefined && savedPages.length >= user.max_active_pages) {
      toast.error(`Limite atingido! Você já tem ${savedPages.length} páginas no seu plano. Delete uma para criar outra.`, { autoClose: 5000 });
      return;
    }

    setSavingToSobeVenda(true);
    try {
      const payload = { name: form.project_name.trim(), slug: form.slug.trim(), page_type: "presell", html_content: generatedHtml };
      let page;
      if (savedPageRecord?.id) {
        page = await apiSobePages(`/pages/${savedPageRecord.id}`, { method: "PATCH", body: JSON.stringify({ pages_id: savedPageRecord.id, ...payload }) });
        toast.success("Página atualizada no SobeVenda.");
      } else {
        page = await apiSobePages("/pages", { method: "POST", body: JSON.stringify(payload) });
        toast.success("Página salva no SobeVenda.");
      }
      setSavedPageRecord(page);
      if (selectedScriptUuid && page?.id) {
        try {
          await apiScript("/link-sobescript-page", { method: "PATCH", body: JSON.stringify({ uuid: selectedScriptUuid, pages_id: page.id }) });
          await loadScripts();
        } catch { toast.warning("Página salva, mas erro ao vincular script."); }
      }
      await loadSavedPages();
    } catch (error) { toast.error(error.message || "Erro ao salvar página no SobeVenda."); }
    finally { setSavingToSobeVenda(false); }
  }

  async function handlePublishToSobeVenda() {
    if (!savedPageRecord?.id) { toast.error("Salve a página no SobeVenda antes de publicar."); return; }
    if (!selectedDomainId) { toast.error("Selecione um domínio conectado."); return; }

    setPublishingToSobeVenda(true);
    try {
      const published = await apiSobePages(`/pages/${savedPageRecord.id}/publish`, {
        method: "POST",
        body: JSON.stringify({ pages_id: savedPageRecord.id, domains_id: Number(selectedDomainId), slug: form.slug?.trim() || "/" }),
      });
      setSavedPageRecord(published);
      await loadSavedPages();
      toast.success("Página publicada!");
    } catch (error) { toast.error(error.message || "Erro ao publicar página."); }
    finally { setPublishingToSobeVenda(false); }
  }

  async function handleCopyPublicUrl() {
    if (!savedPageRecord?.public_url) { toast.error("Nenhuma URL pública gerada ainda."); return; }
    try { await navigator.clipboard.writeText(savedPageRecord.public_url); toast.success("URL copiada."); }
    catch { toast.error("Não foi possível copiar a URL."); }
  }

  async function handleCopyPageUrl(url) {
    if (!url) { toast.error("Essa página ainda não possui URL pública."); return; }
    try { await navigator.clipboard.writeText(url); toast.success("URL copiada."); }
    catch { toast.error("Não foi possível copiar a URL."); }
  }

  async function handleDeletePage() {
    const pageId = confirmDeletePageId;
    setConfirmDeletePageId(null);
    setPageActionLoadingId(pageId);
    try {
      await apiSobePages(`/pages/${pageId}`, { method: "DELETE" });
      toast.success("Página deletada.");
      await loadSavedPages();
      if (savedPageRecord?.id === pageId) { setSavedPageRecord(null); setSelectedDomainId(""); }
    } catch (error) { toast.error(error.message || "Erro ao deletar página."); }
    finally { setPageActionLoadingId(null); }
  }

  async function handleArchivePage() {
    const pageId = confirmArchivePageId;
    setConfirmArchivePageId(null);
    setPageActionLoadingId(pageId);
    try {
      await apiSobePages(`/pages/${pageId}/archive`, { method: "POST", body: JSON.stringify({ pages_id: pageId }) });
      toast.success("Página arquivada.");
      await loadSavedPages();
      if (savedPageRecord?.id === pageId) {
        const updated = await apiSobePages(`/pages/${pageId}`, { method: "GET" });
        setSavedPageRecord(updated);
      }
    } catch (error) { toast.error(error.message || "Erro ao arquivar página."); }
    finally { setPageActionLoadingId(null); }
  }

  const hasConnectedDomain = domains.some((d) => d.is_active === true || d.status === "connected" || d.status === "active");

  const previewLinks = useMemo(() => {
    const t = TRANSLATIONS[form.language] || TRANSLATIONS.en;
    return {
      desktopMain: form.desktop_modal_link || form.product_url || "#",
      mobileMain: form.mobile_modal_link || form.affiliate_link || form.product_url || "#",
      learnMore: form.learn_more_link || form.product_url || "#",
      privacy: form.privacy_link || "#",
      contact: form.contact_link || "#",
      support: form.official_support_link || "#",
      ctaBar: form.cta_bar_link || form.product_url || "#",
      t,
    };
  }, [form]);

  const pagesSummary = useMemo(() => ({
    total: savedPages.length,
    published: savedPages.filter((p) => p.status === "published").length,
  }), [savedPages]);

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="p-4 md:p-6">
      <div className="space-y-6">

        {/* HEADER */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100 flex items-center gap-2">
              <FileCode2 className="w-6 h-6" />
              SobePages
            </h1>
            <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
              Gere a página e publique direto no seu domínio conectado.
            </p>
          </div>
          <button
            onClick={() => { setSavedPageRecord(null); setForm({ ...INITIAL_FORM }); setGeneratedHtml(""); toast.success("Novo projeto iniciado."); }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100 shrink-0"
          >
            <Plus className="w-4 h-4" /> Nova página
          </button>
        </div>

        {/* PUBLICAÇÃO */}
        <div className={`${cardCls} space-y-4`}>
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h2 className="text-sm font-bold text-slate-900 dark:text-slate-100">Publicação no SobeVenda</h2>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">Publique esta página direto no domínio conectado.</p>
            </div>
            {savedPageRecord?.id && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300">
                Editando: {savedPageRecord.name} · ID #{savedPageRecord.id}
              </span>
            )}
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            {/* Domínio */}
            <div>
              <label className={labelCls}>Domínio conectado</label>
              {hasConnectedDomain ? (
                <select className={inputCls} value={selectedDomainId} onChange={(e) => setSelectedDomainId(e.target.value)}>
                  {domains.filter((d) => d.is_active === true || d.status === "connected" || d.status === "active")
                    .map((domain) => <option key={domain.id} value={domain.id}>{domain.domain}</option>)}
                </select>
              ) : (
                <div className="mt-1">
                  <p className="text-xs text-slate-500 dark:text-slate-400 mb-2">Nenhum domínio conectado.</p>
                  <button onClick={() => navigate("/domains")} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-900 text-white text-xs font-semibold hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100">
                    <Globe className="w-3.5 h-3.5" /> Conectar domínio
                  </button>
                </div>
              )}
            </div>

            {/* Status */}
            <div>
              <label className={labelCls}>Status da página</label>
              <div className="mt-1">
                {savedPageRecord?.status
                  ? <PageStatusBadge status={savedPageRecord.status} />
                  : <span className="text-sm text-slate-500 dark:text-slate-400">Ainda não salva</span>}
                {savedPageRecord?.id && <p className="text-xs text-slate-400 mt-1">ID #{savedPageRecord.id}</p>}
              </div>
            </div>

            {/* URL pública */}
            <div>
              <label className={labelCls}>URL pública</label>
              {savedPageRecord?.public_url ? (
                <div className="mt-1">
                  <p className="text-xs text-slate-700 dark:text-slate-300 break-all mb-2">{savedPageRecord.public_url}</p>
                  <button onClick={handleCopyPublicUrl} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 text-xs">
                    <Copy className="w-3.5 h-3.5" /> Copiar URL
                  </button>
                </div>
              ) : (
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">Gerada após publicação.</p>
              )}
            </div>
          </div>

          <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
            <button onClick={handleSaveToSobeVenda} disabled={savingToSobeVenda}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-60 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100">
              <Save className="w-4 h-4" />
              {savingToSobeVenda ? (savedPageRecord?.id ? "Atualizando..." : "Salvando...") : (savedPageRecord?.id ? "Atualizar no SobeVenda" : "Salvar no SobeVenda")}
            </button>
            <button onClick={handlePublishToSobeVenda} disabled={publishingToSobeVenda || !savedPageRecord?.id || !hasConnectedDomain}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 disabled:opacity-50 text-sm">
              <Globe className="w-4 h-4" />
              {publishingToSobeVenda ? "Publicando..." : "Publicar no SobeVenda"}
            </button>
          </div>
        </div>

        {/* PÁGINAS SALVAS */}
        <div className={cardCls}>
          <div className="flex items-center justify-between gap-3 mb-4">
            <div>
              <h2 className="text-sm font-bold text-slate-900 dark:text-slate-100">Páginas salvas</h2>
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                {pagesSummary.total} {pagesSummary.total === 1 ? "página" : "páginas"} · {pagesSummary.published} publicada{pagesSummary.published !== 1 ? "s" : ""}
              </p>
            </div>
            <button onClick={loadSavedPages} disabled={loadingSavedPages}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 text-sm disabled:opacity-50">
              <RefreshCw className={`w-4 h-4 ${loadingSavedPages ? "animate-spin" : ""}`} />
              <span className="hidden sm:inline">Atualizar</span>
            </button>
          </div>

          {user?.max_active_pages !== undefined && savedPages.length >= user.max_active_pages && (
            <p className="text-xs text-slate-500 dark:text-slate-400 mb-4">
              Limite de {user?.max_active_pages} páginas atingido. Delete uma página para criar outra.
            </p>
          )}

          {/* Mobile: Cards */}
          <div className="lg:hidden space-y-3">
            {loadingSavedPages ? (
              <div className="p-4 text-slate-500 text-sm">Carregando páginas...</div>
            ) : savedPages.length === 0 ? (
              <div className="p-4 text-slate-500 text-sm text-center">Nenhuma página salva ainda.</div>
            ) : savedPages.map((page) => {
              const domain = domains.find((d) => d.id === page.domains_id);
              const busy = pageActionLoadingId === page.id;
              const linkedScript = scripts.find((s) => Number(s.pages_id) === Number(page.id));
              return (
                <div key={page.id} className="rounded-lg border border-slate-200 dark:border-slate-700 p-4">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-slate-900 dark:text-slate-100 break-words">{page.name}</div>
                      <div className="text-xs text-slate-500 mt-0.5">{page.slug || "—"}</div>
                    </div>
                    <PageStatusBadge status={page.status} />
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-xs mb-3">
                    <div><span className="text-slate-500">Domínio:</span> <span className="text-slate-700 dark:text-slate-300">{domain?.domain || "—"}</span></div>
                    <div><span className="text-slate-500">Script:</span> <span className="text-slate-700 dark:text-slate-300">{linkedScript?.project_name || "—"}</span></div>
                    {page.public_url && (
                      <div className="col-span-2">
                        <button onClick={() => handleCopyPageUrl(page.public_url)} className="text-slate-600 dark:text-slate-300 truncate max-w-full hover:underline text-left">
                          {page.public_url}
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 pt-2 border-t border-slate-100 dark:border-slate-800">
                    <button onClick={() => handleUsePageInEdition(page)} disabled={busy}
                      className="flex-1 inline-flex items-center justify-center px-3 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 text-xs disabled:opacity-50">
                      Usar
                    </button>
                    <button onClick={() => setConfirmDeletePageId(page.id)} disabled={busy}
                      className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-red-200 dark:border-red-900 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 text-xs disabled:opacity-50">
                      <Trash2 className="w-3.5 h-3.5" /> Deletar
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop: Tabela */}
          <div className="hidden lg:block rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full table-fixed text-sm">
                <thead className="bg-slate-50 dark:bg-slate-800/40">
                  <tr className="text-left">
                    <th className="px-4 py-3 w-[18%] font-semibold text-slate-700 dark:text-slate-200">Nome</th>
                    <th className="px-4 py-3 w-[13%] font-semibold text-slate-700 dark:text-slate-200">Slug</th>
                    <th className="px-4 py-3 w-[10%] font-semibold text-slate-700 dark:text-slate-200">Status</th>
                    <th className="px-4 py-3 w-[12%] font-semibold text-slate-700 dark:text-slate-200">Domínio</th>
                    <th className="px-4 py-3 w-[22%] font-semibold text-slate-700 dark:text-slate-200">URL pública</th>
                    <th className="px-4 py-3 w-[13%] font-semibold text-slate-700 dark:text-slate-200">Script</th>
                    <th className="px-4 py-3 w-[12%] text-right font-semibold text-slate-700 dark:text-slate-200">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {loadingSavedPages ? (
                    <tr><td className="px-4 py-4 text-slate-500 text-sm" colSpan={7}>Carregando páginas...</td></tr>
                  ) : savedPages.length === 0 ? (
                    <tr><td className="px-4 py-4 text-slate-500 text-sm" colSpan={7}>Nenhuma página salva ainda.</td></tr>
                  ) : savedPages.map((page) => {
                    const domain = domains.find((d) => d.id === page.domains_id);
                    const busy = pageActionLoadingId === page.id;
                    const linkedScript = scripts.find((s) => Number(s.pages_id) === Number(page.id));
                    return (
                      <tr key={page.id} className="border-t border-slate-100 dark:border-slate-800 align-middle hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors">
                        <td className="px-4 py-3 font-medium text-slate-900 dark:text-slate-100 break-words">{page.name}</td>
                        <td className="px-4 py-3 text-slate-600 dark:text-slate-300 text-xs">{page.slug || "—"}</td>
                        <td className="px-4 py-3"><PageStatusBadge status={page.status} /></td>
                        <td className="px-4 py-3 text-slate-600 dark:text-slate-300 text-xs">{domain?.domain || "—"}</td>
                        <td className="px-4 py-3">
                          {page.public_url ? (
                            <button onClick={() => handleCopyPageUrl(page.public_url)} className="text-xs text-slate-600 dark:text-slate-300 truncate max-w-full hover:underline text-left block">
                              {page.public_url}
                            </button>
                          ) : <span className="text-slate-400 text-xs">—</span>}
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-600 dark:text-slate-300">{linkedScript?.project_name || "—"}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-2">
                            <button onClick={() => handleUsePageInEdition(page)} disabled={busy}
                              className="inline-flex items-center px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 text-xs disabled:opacity-50">
                              Usar
                            </button>
                            <button onClick={() => setConfirmDeletePageId(page.id)} disabled={busy}
                              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-red-200 dark:border-red-900 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 text-xs disabled:opacity-50">
                              <Trash2 className="w-3.5 h-3.5" /> Deletar
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* EDITOR — GRID */}
        <div className="grid gap-4 xl:grid-cols-2">

          {/* Coluna esquerda */}
          <div className="space-y-4">

            {/* Análise automática */}
            <AccordionSection title="Análise automática" icon={Wand2} isOpen={openSections.analysis} onToggle={() => toggleSection("analysis")}
              rightSlot={detectedData ? <span className="inline-flex items-center gap-1.5 text-xs text-emerald-600 dark:text-emerald-400"><CheckCircle2 className="w-3.5 h-3.5" /> Preenchido</span> : null}>

              <div className="space-y-4">
                {/* Produtos */}
                <div>
                  <label className={labelCls}>Usar produto do SobeVenda</label>
                  {loadingProducts ? (
                    <p className="text-xs text-slate-500">Carregando produtos...</p>
                  ) : products.length > 0 ? (
                    <select className={inputCls} value={selectedProductId} onChange={(e) => handleSelectProduct(e.target.value)}>
                      <option value="">Preencher manualmente</option>
                      {products.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  ) : (
                    <p className="text-xs text-slate-500">Nenhum produto cadastrado. <button onClick={() => navigate("/produtos")} className="underline">Criar produto</button></p>
                  )}
                </div>

                <div className="flex items-center gap-3">
                  <div className="h-px flex-1 bg-slate-200 dark:bg-slate-700" />
                  <span className="text-xs text-slate-400">ou preencha manualmente</span>
                  <div className="h-px flex-1 bg-slate-200 dark:bg-slate-700" />
                </div>

                <div>
                  <label className={labelCls}>Link do produtor</label>
                  <input className={inputCls} value={form.product_url} onChange={(e) => setField("product_url", e.target.value)} placeholder="https://produto.com" />
                </div>

                <div>
                  <label className={labelCls}>Link de afiliado</label>
                  <input className={inputCls} value={form.affiliate_link} onChange={(e) => setField("affiliate_link", e.target.value)} placeholder="https://seulink.com" />
                </div>

                {/* Script */}
                <div>
                  <label className={labelCls}>SobeScript (rastreamento)</label>
                  {loadingScripts ? (
                    <p className="text-xs text-slate-500">Carregando scripts...</p>
                  ) : scripts.length > 0 ? (
                    <>
                      <select className={inputCls} value={selectedScriptUuid} onChange={(e) => {
                        const uuid = e.target.value;
                        setSelectedScriptUuid(uuid);
                        if (uuid) {
                          const s = scripts.find((s) => s.uuid === uuid);
                          if (s?.generated_code) { setField("custom_script", s.generated_code); toast.success(`Script "${s.project_name}" adicionado!`); }
                        } else { setField("custom_script", ""); }
                      }}>
                        <option value="">Nenhum script</option>
                        {scripts.map((s) => <option key={s.uuid} value={s.uuid}>{s.project_name || s.uuid}</option>)}
                      </select>
                      {selectedScriptUuid && <p className="text-xs text-slate-500 mt-1">O código do script será injetado automaticamente no HTML.</p>}
                    </>
                  ) : (
                    <p className="text-xs text-slate-500">Nenhum script disponível. <button onClick={() => navigate("/sobescript")} className="underline">Criar script</button></p>
                  )}
                </div>

                <div className="flex flex-wrap gap-2">
                  <button onClick={handleAnalyzeProductPage} disabled={analysisLoading}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 disabled:opacity-60 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100">
                    <Wand2 className="w-4 h-4" />
                    {analysisLoading ? "Analisando..." : "Analisar página"}
                  </button>
                  <button onClick={handleGenerateHtml}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100">
                    <FileCode2 className="w-4 h-4" /> Gerar HTML
                  </button>
                  <button onClick={handleDownloadZip}
                    className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 text-sm">
                    <Archive className="w-4 h-4" /> Baixar ZIP
                  </button>
                </div>
              </div>
            </AccordionSection>

            {/* Informações da página */}
            <AccordionSection title="Informações da página" icon={Globe} isOpen={openSections.pageInfo} onToggle={() => toggleSection("pageInfo")}>
              <div className="space-y-4">
                {/* Idioma */}
                <div>
                  <label className={labelCls}>Idioma da página</label>
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mt-1">
                    {LANGUAGE_OPTIONS.map((lang) => (
                      <button key={lang.code} type="button" onClick={() => handleLanguageChange(lang.code)}
                        className={`flex flex-col items-center gap-1 rounded-lg border-2 px-2 py-2 text-center transition text-xs ${
                          form.language === lang.code
                            ? "border-slate-900 dark:border-slate-100 bg-slate-50 dark:bg-slate-800 font-semibold"
                            : "border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600"
                        }`}>
                        <span className="text-xl">{lang.flag}</span>
                        <span className="text-slate-700 dark:text-slate-300">{lang.name}</span>
                      </button>
                    ))}
                  </div>
                  <p className="text-xs text-slate-500 mt-2">Ao selecionar um idioma, os textos padrão serão traduzidos automaticamente.</p>
                </div>

                <div className="grid gap-4 md:grid-cols-2">
                  <div>
                    <label className={labelCls}>Nome do projeto</label>
                    <input className={inputCls} value={form.project_name} onChange={(e) => setField("project_name", e.target.value)} placeholder="Ex: ProstaVive Reviews" />
                  </div>
                  <div>
                    <label className={labelCls}>Slug</label>
                    <input className={inputCls} value={form.slug} onChange={(e) => setField("slug", slugify(e.target.value))} placeholder="prostavive-reviews" disabled={!!savedPageRecord?.id} />
                    {savedPageRecord?.id && <p className="text-xs text-slate-500 mt-1">Slug não pode ser alterado em páginas já salvas.</p>}
                  </div>
                  <div>
                    <label className={labelCls}>Nome do arquivo</label>
                    <input className={inputCls} value={form.html_file_name} onChange={(e) => setField("html_file_name", e.target.value)} placeholder="pv.html" />
                  </div>
                  <div>
                    <label className={labelCls}>Robots</label>
                    <input className={inputCls} value={form.robots} onChange={(e) => setField("robots", e.target.value)} placeholder="noindex,nofollow" />
                  </div>
                  <div className="md:col-span-2">
                    <label className={labelCls}>Page Title</label>
                    <input className={inputCls} value={form.page_title} onChange={(e) => setField("page_title", e.target.value)} placeholder="ProstaVive Reviews 2026" />
                  </div>
                  <div className="md:col-span-2">
                    <label className={labelCls}>Meta Description</label>
                    <textarea className={`${inputCls} min-h-[80px] resize-none`} value={form.meta_description} onChange={(e) => setField("meta_description", e.target.value)} placeholder="Descreva a página para SEO." />
                  </div>
                </div>
              </div>
            </AccordionSection>

            {/* Links avançados */}
            <AccordionSection title="Links avançados" icon={Link2} isOpen={openSections.links} onToggle={() => toggleSection("links")}>
              <div className="space-y-4">

                {/* Ação rápida: preencher todos */}
                <div className="flex flex-wrap gap-2 pb-3 border-b border-slate-100 dark:border-slate-800">
                  <span className="text-xs text-slate-500 dark:text-slate-400 self-center">Preencher todos com:</span>
                  <button
                    type="button"
                    onClick={() => {
                      const url = form.product_url || "";
                      setField("desktop_modal_link", url);
                      setField("mobile_modal_link", url);
                      setField("cta_bar_link", url);
                      setField("learn_more_link", url);
                      setField("official_support_link", url);
                      setField("privacy_link", url);
                      setField("contact_link", url);
                    }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 text-xs"
                  >
                    Link do produtor
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      const url = form.affiliate_link || "";
                      setField("desktop_modal_link", url);
                      setField("mobile_modal_link", url);
                      setField("cta_bar_link", url);
                      setField("learn_more_link", url);
                      setField("official_support_link", url);
                      setField("privacy_link", url);
                      setField("contact_link", url);
                    }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 text-xs"
                  >
                    Link de afiliado
                  </button>
                </div>

                {/* Campos com toggle individual */}
                <div className="grid gap-4 md:grid-cols-2">
                  {[
                    { label: "Desktop Modal Link", field: "desktop_modal_link" },
                    { label: "Mobile Modal Link",  field: "mobile_modal_link"  },
                    { label: "CTA principal da barra", field: "cta_bar_link"  },
                    { label: "Learn More Link",    field: "learn_more_link"    },
                    { label: "Official Support Link", field: "official_support_link" },
                  ].map(({ label, field }) => {
                    const isAffiliate = !!form.affiliate_link && form[field] === form.affiliate_link;
                    const isProducer  = !!form.product_url   && form[field] === form.product_url;
                    return (
                      <div key={field} className={field === "official_support_link" ? "md:col-span-2" : ""}>
                        <div className="flex items-center justify-between mb-1">
                          <label className={labelCls + " mb-0"}>{label}</label>
                          <div className="flex items-center gap-1">
                            <button
                              type="button"
                              title="Usar link do produtor"
                              onClick={() => setField(field, form.product_url || "")}
                              className={`px-2 py-0.5 rounded text-[10px] font-medium border transition ${
                                isProducer
                                  ? "border-slate-900 bg-slate-900 text-white dark:border-white dark:bg-white dark:text-slate-900"
                                  : "border-slate-200 dark:border-slate-700 text-slate-500 hover:border-slate-400"
                              }`}
                            >
                              Produtor
                            </button>
                            <button
                              type="button"
                              title="Usar link de afiliado"
                              onClick={() => setField(field, form.affiliate_link || "")}
                              className={`px-2 py-0.5 rounded text-[10px] font-medium border transition ${
                                isAffiliate
                                  ? "border-slate-900 bg-slate-900 text-white dark:border-white dark:bg-white dark:text-slate-900"
                                  : "border-slate-200 dark:border-slate-700 text-slate-500 hover:border-slate-400"
                              }`}
                            >
                              Afiliado
                            </button>
                          </div>
                        </div>
                        <input className={inputCls} value={form[field]} onChange={(e) => setField(field, e.target.value)} placeholder="https://..." />
                      </div>
                    );
                  })}

                  {/* Privacy e Contact — com toggle também */}
                  {[
                    { label: "Privacy Link", field: "privacy_link" },
                    { label: "Contact Link", field: "contact_link" },
                  ].map(({ label, field }) => {
                    const isAffiliate = !!form.affiliate_link && form[field] === form.affiliate_link;
                    const isProducer  = !!form.product_url   && form[field] === form.product_url;
                    return (
                      <div key={field}>
                        <div className="flex items-center justify-between mb-1">
                          <label className={labelCls + " mb-0"}>{label}</label>
                          <div className="flex items-center gap-1">
                            <button type="button" onClick={() => setField(field, form.product_url || "")}
                              className={`px-2 py-0.5 rounded text-[10px] font-medium border transition ${isProducer ? "border-slate-900 bg-slate-900 text-white dark:border-white dark:bg-white dark:text-slate-900" : "border-slate-200 dark:border-slate-700 text-slate-500 hover:border-slate-400"}`}>
                              Produtor
                            </button>
                            <button type="button" onClick={() => setField(field, form.affiliate_link || "")}
                              className={`px-2 py-0.5 rounded text-[10px] font-medium border transition ${isAffiliate ? "border-slate-900 bg-slate-900 text-white dark:border-white dark:bg-white dark:text-slate-900" : "border-slate-200 dark:border-slate-700 text-slate-500 hover:border-slate-400"}`}>
                              Afiliado
                            </button>
                          </div>
                        </div>
                        <input className={inputCls} value={form[field]} onChange={(e) => setField(field, e.target.value)} placeholder="/privacy ou https://..." />
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800">
                <label className={labelCls}>Código de rastreamento (SobeScript)</label>
                <p className="text-xs text-slate-500 mb-2">Esse código será inserido no início do head da página gerada.</p>
                <textarea
                  className={`${inputCls} min-h-[160px] font-mono text-xs leading-6 resize-none`}
                  value={form.custom_script}
                  onChange={(e) => setField("custom_script", e.target.value)}
                  placeholder={`Cole aqui o seu código de rastreamento:\n<script>\n  console.log("sobescript");\n</script>`}
                />
              </div>
            </AccordionSection>
          </div>

          {/* Coluna direita */}
          <div className="space-y-4">

            {/* Visual e conteúdo */}
            <AccordionSection title="Visual e conteúdo" icon={ImageIcon} isOpen={openSections.visuals} onToggle={() => toggleSection("visuals")}>
              <div className="grid gap-4 md:grid-cols-2">
                <div className="md:col-span-2">
                  <label className={labelCls}>Background Desktop</label>
                  <input className={inputCls} value={form.bg_desktop} onChange={(e) => setField("bg_desktop", e.target.value)} placeholder="https://..." />
                </div>
                <div>
                  <label className={labelCls}>Background Tablet</label>
                  <input className={inputCls} value={form.bg_tablet} onChange={(e) => setField("bg_tablet", e.target.value)} placeholder="https://..." />
                </div>
                <div>
                  <label className={labelCls}>Background Mobile</label>
                  <input className={inputCls} value={form.bg_mobile} onChange={(e) => setField("bg_mobile", e.target.value)} placeholder="https://..." />
                </div>
                <div>
                  <label className={labelCls}>BG Size</label>
                  <select className={inputCls} value={form.bg_size} onChange={(e) => setField("bg_size", e.target.value)}>
                    <option value="cover">cover</option>
                    <option value="contain">contain</option>
                    <option value="auto">auto</option>
                  </select>
                </div>
                <div>
                  <label className={labelCls}>CTA Label</label>
                  <input className={inputCls} value={form.cta_label} onChange={(e) => setField("cta_label", e.target.value)} placeholder="Go to Official Site" />
                </div>
                <div>
                  <label className={labelCls}>Overlay Desktop</label>
                  <input className={inputCls} value={form.overlay_desktop} onChange={(e) => setField("overlay_desktop", e.target.value)} placeholder="0.70" />
                </div>
                <div>
                  <label className={labelCls}>Overlay Mobile</label>
                  <input className={inputCls} value={form.overlay_mobile} onChange={(e) => setField("overlay_mobile", e.target.value)} placeholder="0.60" />
                </div>
                <div className="md:col-span-2">
                  <label className={labelCls}>Cookie Title</label>
                  <input className={inputCls} value={form.cookie_title} onChange={(e) => setField("cookie_title", e.target.value)} placeholder="We use cookies!" />
                </div>
                <div className="md:col-span-2">
                  <div className="flex items-center justify-between mb-1">
                    <label className={labelCls + " mb-0"}>Cookie Text</label>
                    <button type="button" onClick={() => setField("cookie_text", (TRANSLATIONS[form.language] || TRANSLATIONS.en).cookie_text)} className="text-[10px] text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 underline">
                      Restaurar padrão
                    </button>
                  </div>
                  <textarea className={`${inputCls} min-h-[80px] resize-none`} value={form.cookie_text} onChange={(e) => setField("cookie_text", e.target.value)} placeholder={(TRANSLATIONS[form.language] || TRANSLATIONS.en).cookie_text} />
                </div>
                <div className="md:col-span-2">
                  <div className="flex items-center justify-between mb-1">
                    <label className={labelCls + " mb-0"}>Disclosure Text</label>
                    <button type="button" onClick={() => setField("disclosure_text", (TRANSLATIONS[form.language] || TRANSLATIONS.en).disclosure_text)} className="text-[10px] text-slate-500 hover:text-slate-700 dark:hover:text-slate-300 underline">
                      Restaurar padrão
                    </button>
                  </div>
                  <textarea className={`${inputCls} min-h-[96px] resize-none`} value={form.disclosure_text} onChange={(e) => setField("disclosure_text", e.target.value)} placeholder={(TRANSLATIONS[form.language] || TRANSLATIONS.en).disclosure_text} />
                </div>
                <div className="md:col-span-2">
                  <label className={labelCls}>SEO Block</label>
                  <textarea className={`${inputCls} min-h-[160px] resize-none`} value={form.seo_block} onChange={(e) => setField("seo_block", e.target.value)} placeholder="Cole aqui os parágrafos para o bloco SEO oculto. Separe por linhas em branco." />
                </div>
              </div>
            </AccordionSection>

            {/* Preview */}
            <AccordionSection title="Preview estrutural" icon={FileCode2} isOpen={openSections.preview} onToggle={() => toggleSection("preview")}>
              <div className="overflow-hidden rounded-lg border border-slate-200 dark:border-slate-700">
                <div className="relative min-h-[480px] bg-black"
                  style={{
                    backgroundImage: form.bg_desktop
                      ? `linear-gradient(rgba(0,0,0,${form.overlay_desktop}), rgba(0,0,0,${form.overlay_desktop})), url('${form.bg_desktop}')`
                      : "linear-gradient(rgba(15,23,42,.88), rgba(15,23,42,.88))",
                    backgroundPosition: "top center", backgroundRepeat: "no-repeat", backgroundSize: form.bg_size || "cover",
                  }}>
                  <div className="absolute inset-0 flex items-center justify-center p-4">
                    <div className="w-full max-w-[320px] rounded-[18px] bg-white px-5 py-6 text-center shadow-xl">
                      <div className="text-3xl">🍪</div>
                      <h3 className="mt-3 text-base font-bold text-slate-900">{form.cookie_title || "We use cookies!"}</h3>
                      <p className="mt-2 text-sm text-slate-600">{form.cookie_text || "This website uses cookies to ensure you get the best experience."} <a href={previewLinks.learnMore} className="text-blue-500 underline">{previewLinks.t.learn_more}</a></p>
                      <div className="mt-4 flex justify-center gap-2">
                        <span className="rounded-lg bg-slate-100 px-4 py-2 text-sm font-semibold text-slate-700">{previewLinks.t.decline}</span>
                        <span className="rounded-lg bg-blue-500 px-4 py-2 text-sm font-semibold text-white">{previewLinks.t.accept}</span>
                      </div>
                    </div>
                  </div>
                  <div className="absolute bottom-3 left-1/2 w-[calc(100%-24px)] -translate-x-1/2 rounded-xl bg-white p-3 shadow-xl">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="text-xs text-slate-700 max-w-[300px]">
                        <strong>{previewLinks.t.disclosure_label}</strong> {form.disclosure_text || previewLinks.t.disclosure_text}
                      </div>
                      <div className="flex flex-wrap gap-1 text-xs">
                        <span className="rounded border border-slate-200 px-2 py-1 text-slate-700">{previewLinks.t.privacy}</span>
                        <span className="rounded border border-slate-200 px-2 py-1 text-slate-700">{previewLinks.t.contact}</span>
                        <span className="rounded bg-slate-900 px-2 py-1 font-semibold text-white">{form.cta_label || previewLinks.t.cta_label}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </AccordionSection>

            {/* HTML */}
            <AccordionSection title="Código HTML" icon={Copy} isOpen={openSections.html} onToggle={() => toggleSection("html")}
              rightSlot={
                <div className="hidden sm:flex items-center gap-2">
                  <button onClick={(e) => { e.stopPropagation(); handleCopyHtml(); }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 text-xs">
                    <Copy className="w-3.5 h-3.5" /> Copiar
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); handleDownloadHtml(); }}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-50 dark:hover:bg-slate-800 text-xs">
                    <Download className="w-3.5 h-3.5" /> Baixar
                  </button>
                </div>
              }>
              <textarea className={`${inputCls} min-h-[400px] font-mono text-xs resize-none`} value={generatedHtml} readOnly placeholder="O HTML gerado aparecerá aqui." />
            </AccordionSection>
          </div>
        </div>
      </div>

      {/* Confirm Dialogs */}
      <ConfirmDialog
        open={confirmDeletePageId !== null}
        title="Deletar página permanentemente"
        message="Esta ação é irreversível. A página será removida do banco de dados e do Cloudflare. Tem certeza?"
        confirmLabel="Deletar"
        onConfirm={handleDeletePage}
        onCancel={() => setConfirmDeletePageId(null)}
      />
      <ConfirmDialog
        open={confirmArchivePageId !== null}
        title="Arquivar página"
        message="Tem certeza que deseja arquivar esta página?"
        confirmLabel="Arquivar"
        onConfirm={handleArchivePage}
        onCancel={() => setConfirmArchivePageId(null)}
      />
    </div>
  );
}